import { useCallback, useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { getDeviceId } from "@/lib/deviceId";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { confirmDialog, promptDialog } from "@/lib/confirmDialog";
import {
  MonitorSmartphone,
  KeyRound,
  Trash2,
  Pencil,
  Unlock,
  ShieldCheck,
  Copy,
  ShieldAlert,
  CheckCircle2,
} from "lucide-react";

function _fmt(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString("el-GR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return dt; }
}

function _daysSince(dt) {
  if (!dt) return null;
  try {
    const d = (Date.now() - new Date(dt).getTime()) / 86400000;
    return d < 1 ? "σήμερα" : `${Math.floor(d)} ημέρες`;
  } catch { return null; }
}

function _fmtCountdown(sec) {
  if (sec <= 0) return "λήξη";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${h}ω ${m}λ`;
}

export default function DevicesTab() {
  const [devices, setDevices] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [codeDlg, setCodeDlg] = useState(null); // { code, expires_at, ttl_minutes, label_hint }
  const [labelHint, setLabelHint] = useState("");
  const [genBusy, setGenBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingLabel, setEditingLabel] = useState("");

  const currentDeviceId = useMemo(() => getDeviceId(), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [devR, stR] = await Promise.all([
        http.get("/devices"),
        http.get("/devices/status"),
      ]);
      setDevices(devR.data || []);
      setStatus(stR.data || null);
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || "Αποτυχία φόρτωσης");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generateCode = async () => {
    setGenBusy(true);
    try {
      const r = await http.post("/devices/generate-code", {
        label_hint: labelHint.trim() || null,
      });
      setCodeDlg(r.data);
      setLabelHint("");
      toast.success("Δημιουργήθηκε νέος κωδικός");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    } finally {
      setGenBusy(false);
    }
  };

  const revoke = async (dev) => {
    if (!(await confirmDialog(`Ανάκληση της συσκευής «${dev.label || dev.device_id}»;`))) return;
    try {
      await http.delete(`/devices/${dev.device_id}`);
      toast.success("Ανακλήθηκε");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    }
  };

  const unlock = async (dev) => {
    try {
      await http.post(`/devices/${dev.device_id}/unlock`);
      toast.success("Ξεκλείδωσε");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    }
  };

  const startRelabel = (dev) => {
    setEditingId(dev.device_id);
    setEditingLabel(dev.label || "");
  };

  const saveLabel = async (dev) => {
    if (!editingLabel.trim()) { toast.error("Δώσε ετικέτα"); return; }
    try {
      await http.patch(`/devices/${dev.device_id}`, { label: editingLabel.trim() });
      toast.success("Ενημερώθηκε");
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    }
  };

  const toggleGate = async (enabled) => {
    if (enabled) {
      const ok = await confirmDialog(
        "Ενεργοποίηση Πύλης Συσκευής; Μόνο καταχωρημένες συσκευές θα βλέπουν το πρόγραμμα. Η συσκευή σου εγκρίνεται αυτόματα."
      );
      if (!ok) return;
    }
    try {
      await http.post("/devices/toggle", { enabled });
      localStorage.removeItem("gate_tenant");
      toast.success(enabled ? "Η Πύλη Συσκευής ενεργοποιήθηκε" : "Η Πύλη Συσκευής απενεργοποιήθηκε");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    }
  };

  if (loading) return <div className="text-slate-500 py-8 text-center">Φόρτωση συσκευών…</div>;
  if (error) return <div className="text-rose-600 py-8 text-center" data-testid="devices-error">{error}</div>;

  return (
    <div className="space-y-5" data-testid="devices-tab">
      {/* Πύλη Συσκευής toggle (Ιούν 2026) */}
      <Card className="p-4 border-2 border-cyan-200">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <div className="font-bold text-slate-800">Πύλη Συσκευής</div>
            <div className="text-xs text-slate-600 mt-0.5">
              Όταν είναι ενεργή, μη καταχωρημένες συσκευές που ανοίγουν τη σελίδα βλέπουν
              ΜΟΝΟ την οθόνη «Καταχώρηση Συσκευής» — χρειάζονται 8-ψήφιο κωδικό (μία φορά).
              Η δική σου συσκευή εγκρίνεται αυτόματα κατά την ενεργοποίηση.
            </div>
          </div>
          <Switch
            checked={!!status?.enabled}
            onCheckedChange={toggleGate}
            data-testid="devices-gate-toggle"
          />
        </div>
      </Card>

      {/* Status / grace banner */}
      <Card className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full grid place-items-center ${status?.grace_active ? "bg-amber-100" : "bg-emerald-100"}`}>
            {status?.grace_active
              ? <ShieldAlert className="text-amber-700" size={20} />
              : <ShieldCheck className="text-emerald-700" size={20} />}
          </div>
          <div className="flex-1 text-sm">
            <div className="font-bold text-slate-800">
              {status?.grace_active
                ? "Περίοδος χάριτος ενεργή — αυτόματη έγκριση νέων συσκευών"
                : "Πλήρης προστασία — μόνο εγκεκριμένες συσκευές μπορούν να συνδεθούν"}
            </div>
            <div className="text-slate-600 mt-0.5">
              {status?.grace_active ? (
                <>
                  Οι συσκευές που θα κάνουν login τις επόμενες{" "}
                  <b>{_fmtCountdown(status.grace_remaining_seconds)}</b> εγκρίνονται αυτόματα.
                </>
              ) : (
                <>
                  Κάθε νέα συσκευή θα ζητά 8-ψήφιο κωδικό. Αδράνεια &gt; {status?.idle_days ?? 30} ημέρες
                  ή {status?.max_wrong_pins ?? 5} λάθος PIN → auto-lock.
                </>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Generate code panel */}
      <Card className="p-4 bg-slate-50 border-2 border-dashed border-slate-300">
        <div className="flex items-center gap-3 mb-3">
          <KeyRound className="text-amber-600" size={20} />
          <div className="font-bold text-slate-800">Δημιουργία κωδικού έγκρισης</div>
        </div>
        <div className="text-xs text-slate-600 mb-3">
          Ο 8-ψήφιος κωδικός εμφανίζεται ΜΙΑ φορά. Δώσε τον στον operator της νέας συσκευής.
          Ισχύει 30 λεπτά, μία χρήση.
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="Όνομα συσκευής (προαιρετικό — π.χ. Ταμείο Α)"
            value={labelHint}
            onChange={(e) => setLabelHint(e.target.value.slice(0, 40))}
            data-testid="devices-label-hint"
            maxLength={40}
            className="flex-1"
          />
          <Button
            onClick={generateCode}
            disabled={genBusy}
            data-testid="devices-generate-code-btn"
            className="bg-amber-600 hover:bg-amber-700"
          >
            <KeyRound size={14} className="mr-1" />
            {genBusy ? "…" : "Δημιουργία"}
          </Button>
        </div>
      </Card>

      {/* Devices list */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="font-bold text-slate-700 flex items-center gap-2">
            <MonitorSmartphone size={16} />
            Εγκεκριμένες Συσκευές ({devices.length})
          </div>
          <div className="text-[10px] text-slate-500 font-mono truncate max-w-[280px]" title={currentDeviceId}>
            ID αυτής: {currentDeviceId.slice(0, 16)}…
          </div>
        </div>

        {devices.length === 0 ? (
          <Card className="p-6 text-center text-sm text-slate-500">
            Καμία εγκεκριμένη συσκευή ακόμη.
          </Card>
        ) : (
          <div className="space-y-2" data-testid="devices-list">
            {devices.map((dev) => {
              const isCurrent = dev.device_id === currentDeviceId;
              const isEditing = editingId === dev.device_id;
              const isLocked = !!dev.locked;
              return (
                <Card
                  key={dev.device_id}
                  className={`p-3 flex items-center gap-3 ${
                    isLocked ? "border-rose-300 bg-rose-50" : isCurrent ? "border-emerald-300 bg-emerald-50" : ""
                  }`}
                  data-testid={`device-row-${dev.device_id}`}
                >
                  <div className={`w-10 h-10 rounded-md grid place-items-center flex-shrink-0 ${
                    isLocked ? "bg-rose-200 text-rose-800" : "bg-slate-200 text-slate-700"
                  }`}>
                    {isLocked ? <ShieldAlert size={18} /> : <MonitorSmartphone size={18} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <Input
                        value={editingLabel}
                        onChange={(e) => setEditingLabel(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && saveLabel(dev)}
                        autoFocus
                        maxLength={80}
                        className="h-7 mb-1"
                        data-testid={`device-relabel-input-${dev.device_id}`}
                      />
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="font-bold text-slate-800 truncate">
                          {dev.label || "Συσκευή"}
                        </div>
                        {isCurrent && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-200 text-emerald-800 font-bold">
                            ΤΩΡΑ
                          </span>
                        )}
                        {isLocked && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-600 text-white font-bold">
                            ΚΛΕΙΔΩΜΕΝΗ
                          </span>
                        )}
                        {dev.auto_trusted && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                            auto
                          </span>
                        )}
                      </div>
                    )}
                    <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                      <span>Τελ. χρήση: {_fmt(dev.last_seen_at)}</span>
                      {_daysSince(dev.last_seen_at) && <span>({_daysSince(dev.last_seen_at)})</span>}
                      {dev.approved_by && <span>Έγκριση: {dev.approved_by}</span>}
                      {dev.wrong_pin_count > 0 && !isLocked && (
                        <span className="text-amber-700 font-bold">
                          Λάθος PIN: {dev.wrong_pin_count}/5
                        </span>
                      )}
                      {isLocked && dev.lock_reason && (
                        <span className="text-rose-700 font-bold">Λόγος: {dev.lock_reason}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {isLocked && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => unlock(dev)}
                        data-testid={`device-unlock-btn-${dev.device_id}`}
                        className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                      >
                        <Unlock size={14} className="mr-1" /> Ξεκλείδωμα
                      </Button>
                    )}
                    {isEditing ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => saveLabel(dev)}
                          data-testid={`device-relabel-save-${dev.device_id}`}
                        >
                          Αποθήκευση
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingId(null)}
                        >
                          Άκυρο
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => startRelabel(dev)}
                          data-testid={`device-relabel-btn-${dev.device_id}`}
                          title="Μετονομασία"
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => revoke(dev)}
                          data-testid={`device-revoke-btn-${dev.device_id}`}
                          className="text-rose-600 hover:bg-rose-50"
                          disabled={isCurrent}
                          title={isCurrent ? "Δεν μπορείς να ανακαλέσεις την τρέχουσα" : "Ανάκληση"}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Show-code-once dialog */}
      <Dialog open={!!codeDlg} onOpenChange={(o) => !o && setCodeDlg(null)}>
        <DialogContent className="max-w-md" data-testid="devices-code-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="text-emerald-600" size={20} />
              Κωδικός Έγκρισης
            </DialogTitle>
            <DialogDescription>
              Δώσε τον στη νέα συσκευή. Εμφανίζεται <b>ΜΙΑ φορά</b>. Ισχύει {codeDlg?.ttl_minutes || 30} λεπτά.
            </DialogDescription>
          </DialogHeader>
          {codeDlg && (
            <div className="space-y-3 py-2">
              <div
                className="bg-amber-50 border-2 border-amber-400 rounded-lg py-4 px-6 text-center"
                data-testid="devices-code-display"
              >
                <div className="text-[11px] uppercase tracking-wider text-amber-700 font-bold mb-2">
                  Πληκτρολόγησε στη νέα συσκευή
                </div>
                <div className="text-4xl font-mono font-bold tracking-[0.3em] text-slate-900 tabular-nums">
                  {codeDlg.code}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(codeDlg.code);
                    toast.success("Αντιγράφηκε");
                  }}
                  className="text-xs text-amber-700 hover:text-amber-900 mt-2 inline-flex items-center gap-1"
                  data-testid="devices-code-copy"
                >
                  <Copy size={12} /> Αντιγραφή
                </button>
              </div>
              <div className="flex justify-center">
                <QrCodeBlock
                  payload={`${window.location.origin}/?gate_tenant=${encodeURIComponent(
                    (localStorage.getItem("tenant_code") || localStorage.getItem("gate_tenant") || "").toUpperCase()
                  )}&gate_code=${codeDlg.code}`}
                />
              </div>
              <div className="text-[11px] text-slate-500 text-center">
                Σκάναρε το QR με την κάμερα της νέας συσκευής — ανοίγει τη σελίδα και
                καταχωρεί τη συσκευή αυτόματα.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={() => { setCodeDlg(null); load(); }}
              data-testid="devices-code-dialog-close"
            >
              Έγινε
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QrCodeBlock({ payload }) {
  const [dataUrl, setDataUrl] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const QR = await import("qrcode");
        const url = await QR.toDataURL(payload, { width: 220, margin: 1 });
        if (!cancelled) setDataUrl(url);
      } catch (e) {
        if (!cancelled) setError(e?.message || "QR unavailable");
      }
    })();
    return () => { cancelled = true; };
  }, [payload]);
  if (error) return <div className="text-xs text-slate-400">QR: {error}</div>;
  if (!dataUrl) return <div className="w-[220px] h-[220px] bg-slate-100 grid place-items-center text-xs text-slate-400">…</div>;
  // eslint-disable-next-line
  return <img src={dataUrl} alt="QR κωδικός" className="rounded-md border border-slate-200" data-testid="devices-code-qr" />;
}
